import React from "react";
import { isNil } from "lodash";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { IconButton } from "@mui/material";
import { Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";

import { OPTION } from "../../consts/sentryIds";
import { LOCATION, MATRIX_MODES } from "../../consts/strings";
import {
  COLLECTIONS_PLAN_TYPE,
  DELIVERY_PLAN_TYPES,
  LOCATIONS_PLAN_TYPE,
  OVERRIDES_PLAN_TYPE,
  PICKUP_PLAN_TYPE,
} from "../../consts/planTypes";
import { POSTCODE } from "../../consts/layers";
import { DEFAULT_COLLECTION_TYPES, PLANNED } from "../../consts/collections";

import { View } from "../View";
import { RouteLabel } from "./RouteLabel";
import { CollectionTimesLabel } from "./CollectionTimesLabel";
import { OverrideLabel } from "./OverrideLabel";
import { PickupLabel } from "./PickupLabel";

const useStyles = makeStyles({
  container: {
    height: "100%",
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
  },
  detailsContainer: {
    alignSelf: "center",
    height: "28px",
    padding: "1px",
    display: "flex",
    justifyContent: "center",
    alignContent: "center",
    alignItems: "center",
  },
});

export const AutocompleteItem = ({
  option,
  id,
  type,
  matrixMode,
  hideEditButton,
  planType,
  collectionType,
  showCollectionDefaults,
  routeEdit,
  routes,
  selectedPostcodes,
  onEdit,
}) => {
  const styles = useStyles();
  const { value, routeCode, collection = {}, overrides, pickupConfig } = option;
  const { ROUTE_CODE_KEY, DEFAULT_ROUTE_CODE_KEY } =
    DEFAULT_COLLECTION_TYPES[collectionType || PLANNED];
  const labelRouteCode =
    planType !== COLLECTIONS_PLAN_TYPE
      ? routeCode
      : collection[ROUTE_CODE_KEY] ||
        (showCollectionDefaults && collection[DEFAULT_ROUTE_CODE_KEY]);

  const hasCollectionValue = (collection) =>
    collection.sameDayCutOff ||
    collection.earliestReadyTime ||
    collection.latestReadyTime ||
    !isNil(collection.saturnAuto);

  const renderLabel = () => {
    if (planType === OVERRIDES_PLAN_TYPE)
      return (
        overrides && (
          <OverrideLabel
            routeEdit={routeEdit}
            routes={routes}
            overrides={overrides}
          />
        )
      );
    else if (planType === PICKUP_PLAN_TYPE)
      return pickupConfig && <PickupLabel pickupConfig={pickupConfig} />;
    else if (matrixMode === MATRIX_MODES.COLLECTION_TIMES_EDIT)
      return (
        hasCollectionValue(collection) && (
          <CollectionTimesLabel collection={collection} />
        )
      );
    else
      return (
        <RouteLabel
          routeCode={labelRouteCode}
          disableTooltip={planType === COLLECTIONS_PLAN_TYPE}
        />
      );
  };

  const renderButton = () => {
    if (hideEditButton) return null;

    const isAreaForEditedRoute =
      matrixMode === MATRIX_MODES.EDIT_ONE_ROUTE &&
      (([OVERRIDES_PLAN_TYPE, PICKUP_PLAN_TYPE].includes(planType) &&
        selectedPostcodes?.includes(value)) ||
        (planType === COLLECTIONS_PLAN_TYPE &&
          collection[ROUTE_CODE_KEY] === routeEdit?.routeCode) ||
        (planType === LOCATIONS_PLAN_TYPE &&
          labelRouteCode === routeEdit?.routeCode) ||
        (DELIVERY_PLAN_TYPES.includes(planType) &&
          labelRouteCode === routeEdit?.routeCode));

    return (
      <IconButton
        aria-label={!isAreaForEditedRoute ? "add" : "remove"}
        size={"small"}
        onClick={(e) => {
          e.stopPropagation();
          onEdit(option);
        }}
      >
        {!isAreaForEditedRoute ? <AddIcon /> : <RemoveIcon />}
      </IconButton>
    );
  };

  return (
    <View className={styles.container}>
      <Typography sentryId={id && `${id}__${OPTION}`} noWrap>
        {value}
      </Typography>
      {[POSTCODE, LOCATION].includes(type) && (
        <View className={styles.detailsContainer}>
          {renderLabel()}
          {renderButton()}
        </View>
      )}
    </View>
  );
};
